import { ref, computed, watch } from 'vue';
import axios from 'axios';
import { authState } from '@/utils/auth';
import { emailLocalPart } from '@/utils/emailLocalPart';

const FALLBACK_LABEL = 'Account';

export function useUserMenuIdentity() {
  const firstName = ref('');
  const displayName = ref('');
  const email = ref('');

  function resetIdentity() {
    firstName.value = '';
    displayName.value = '';
    email.value = '';
  }

  async function loadIdentity() {
    if (!authState.isAuthenticated) {
      resetIdentity();
      return;
    }

    const [profileResult, authResult] = await Promise.allSettled([
      axios.get('/api/v1/profile/me'),
      axios.get('/api/v1/auth/me'),
    ]);

    if (authResult.status === 'fulfilled') {
      const account = authResult.value.data;
      email.value = account?.email || '';
      firstName.value = account?.first_name?.trim() || '';
    } else {
      email.value = '';
      firstName.value = '';
    }

    if (profileResult.status === 'fulfilled') {
      const profile = profileResult.value.data;
      // Prefer auth/me first_name; profile also exposes canonical first_name.
      if (!firstName.value) {
        firstName.value = profile?.first_name?.trim() || '';
      }
      displayName.value = profile?.display_name?.trim() || '';
    } else {
      displayName.value = '';
    }
  }

  const menuLabel = computed(() => {
    if (firstName.value) {
      return firstName.value;
    }
    if (displayName.value) {
      return displayName.value;
    }
    const local = emailLocalPart(email.value);
    if (local) {
      return local;
    }
    return FALLBACK_LABEL;
  });

  watch(
    () => authState.isAuthenticated,
    (isAuthenticated) => {
      if (isAuthenticated) {
        loadIdentity();
      } else {
        resetIdentity();
      }
    },
    { immediate: true },
  );

  return {
    menuLabel,
    loadIdentity,
    resetIdentity,
  };
}
